import { computed, onUnmounted, ref } from 'vue'

/**
 * Makes a background poll visible.
 *
 * Vote progress is polled for the whole app (see `useVoteActivity`) and drawn by
 * the orders table. Until now that happened in total silence: numbers moved
 * under the user with no signal that a fetch had run, no way to tell "nothing is
 * happening" from "the poll died", and no idea when the next one was due. This
 * owns the two pieces of state that fix it — an in-flight flag for the sweep
 * bar, and a ticking "how long ago" label — so the table view can stay dumb and
 * just render them.
 *
 * Four decisions here are less obvious than they look:
 *
 * 1. A DEPTH COUNTER, NOT A BOOLEAN. Refreshes overlap in practice: the 30s
 *    interval can fire while the visibilitychange handler's fetch is still in
 *    the air, and the manual button can land on top of either. This is exactly
 *    how `orders/listOrders` desyncs `store.state.orders.loading` — that action
 *    commits a naive `toggleLoading` at both ends, so two overlapping dispatches
 *    flip it four times in the wrong order and the flag sticks on (or off) until
 *    something else happens to flip it back. Counting instead of toggling means
 *    the bar hides when the LAST request settles, not the first.
 *
 * 2. A MINIMUM VISIBLE DURATION. A warm poll can come back in ~150ms. Showing a
 *    bar for 150ms every 30s is a strobe, not information — it reads as a
 *    rendering glitch. The bar is therefore held for one full sweep of the
 *    animation, so every refresh looks like one deliberate pass rather than a
 *    truncated twitch. The alternative — only showing the bar once a fetch has
 *    been slow for >200ms — was rejected because on a fast connection the user
 *    would then never see the poll at all, which is the whole thing we are
 *    trying to fix.
 *
 * 3. THE TIMESTAMP ONLY MOVES ON SUCCESS. `markUpdated()` is called from the
 *    resolve path, never from `finally`. A failed poll must leave the label
 *    ageing so it can go stale and say so — claiming "Updated just now" after a
 *    request that returned nothing would be a lie told by the UI.
 *
 * 4. THE CLOCK TICKS EVERY SECOND. Coarser ticking is cheaper but wrong in a
 *    visible way: "Updated just now" would sit there for up to 15s after it had
 *    stopped being true, and a label you cannot trust is worse than no label.
 *    One `setInterval` writing one ref is cheap; this page already runs a 1s
 *    timer for the ETA countdown.
 */
export function useRefreshPulse(options = {}) {
  /**
   * Long enough that a warm poll still reads as a deliberate event rather than a
   * flicker — a spinner that appears and vanishes inside 150ms reads as a
   * rendering glitch rather than as feedback.
   */
  const minVisibleMs = options.minVisibleMs ?? 1000

  /**
   * Unwraps an option that may be a plain number, a ref/computed, or a getter.
   *
   * The cadence is no longer a constant: the app-wide poller runs at 30s on the
   * jobs page and 105s everywhere else, and switches between the two on a route
   * change. Reading through this inside the computeds below means the ring and
   * the labels track that change on their own — the alternative, re-creating the
   * composable whenever the cadence changed, would throw away `lastUpdatedAt`
   * and make the "Updated Ns ago" label restart from nothing on every
   * navigation.
   */
  const read = (source, fallback) => {
    if (typeof source === 'function') return source()
    if (source && typeof source === 'object' && 'value' in source) return source.value
    return source ?? fallback
  }

  /**
   * How long a full poll cycle lasts. Only used to draw the countdown ring — the
   * actual scheduling lives in `useVoteActivity`, which calls `startCycle()`
   * each time it (re)arms the timer. Passing the value in rather than reading a
   * shared constant keeps this composable free of any assumption about who polls
   * it.
   */
  const pollIntervalMs = () => read(options.pollIntervalMs, 30_000)

  /**
   * Anything older than this and the poll is visibly not getting through.
   *
   * Derived from the cadence rather than hard-coded at 90s, because the cadence
   * is now variable: a fixed 90s threshold would light the "stale" warning on
   * arrival at the jobs page after any spell on a 105s page, describing a poll
   * that had in fact run exactly on time.
   */
  const staleAfterMs = () => read(options.staleAfterMs, pollIntervalMs() * 3)

  /** How many refreshes are currently in the air. See note 1. */
  let pending = 0

  /** What the UI binds to. Lags `pending` by up to `minVisibleMs`. See note 2. */
  const refreshing = ref(false)

  /** Epoch ms of the last SUCCESSFUL refresh. See note 3. */
  const lastUpdatedAt = ref(null)

  /**
   * Epoch ms the current poll cycle was armed. Distinct from `lastUpdatedAt`:
   * the cycle restarts whenever the timer is (re)armed, including after a
   * refresh that FAILED, because the next attempt is still 30s away. Tying the
   * ring to `lastUpdatedAt` instead would leave it sitting empty and accusing
   * after one dropped request, while a retry was in fact scheduled all along.
   */
  const cycleStartedAt = ref(null)

  const now = ref(Date.now())

  let shownAt = 0
  let hideTimer = null

  const settle = () => {
    if (pending > 0) return

    const held = Date.now() - shownAt
    if (held >= minVisibleMs) {
      refreshing.value = false
      return
    }

    clearTimeout(hideTimer)
    hideTimer = setTimeout(() => {
      hideTimer = null
      // Re-checked rather than assumed: a new refresh may have started while
      // this timer was waiting out the remainder of the minimum duration.
      if (pending === 0) refreshing.value = false
    }, minVisibleMs - held)
  }

  /**
   * Wrap a refresh promise so the bar is up for its whole life.
   *
   * The passed promise is returned untouched rather than a `.finally()` chain
   * of it: a chain would re-reject, and a caller that fires and forgets would
   * then trip the global unhandled-rejection handler. The bookkeeping hangs off
   * a side chain that handles both outcomes, which also means the original
   * promise always has a rejection handler attached even when nobody awaits it.
   */
  const track = (promise) => {
    if (pending === 0) {
      clearTimeout(hideTimer)
      hideTimer = null
      shownAt = Date.now()
      refreshing.value = true
    }
    pending += 1

    const done = () => {
      pending = Math.max(0, pending - 1)
      settle()
    }

    const settled = Promise.resolve(promise)
    settled.then(done, done)
    return settled
  }

  /** Call from the success path of a refresh, not from `finally`. */
  const markUpdated = () => {
    lastUpdatedAt.value = Date.now()
    now.value = lastUpdatedAt.value
  }

  /** Call whenever the poll timer is armed or re-armed. Resets the ring to full. */
  const startCycle = () => {
    cycleStartedAt.value = Date.now()
    now.value = cycleStartedAt.value
  }

  /** Call when the cycle is suspended (hidden tab). Blanks the ring. */
  const stopCycle = () => {
    cycleStartedAt.value = null
  }

  /**
   * How much of the current cycle is LEFT, 1 → 0. Drives the countdown ring.
   *
   * Clamped at both ends: a cycle that has overrun (a slow request, or a tab
   * that was throttled in the background) would otherwise drive the arc past
   * empty and start drawing it backwards.
   */
  const nextRefreshFraction = computed(() => {
    if (cycleStartedAt.value === null) return null
    const elapsed = now.value - cycleStartedAt.value
    return Math.min(1, Math.max(0, 1 - elapsed / pollIntervalMs()))
  })

  const secondsToNextRefresh = computed(() => {
    if (nextRefreshFraction.value === null) return null
    return Math.ceil((nextRefreshFraction.value * pollIntervalMs()) / 1000)
  })

  const secondsSinceUpdate = computed(() => {
    if (lastUpdatedAt.value === null) return null
    return Math.max(0, Math.round((now.value - lastUpdatedAt.value) / 1000))
  })

  /**
   * Rounded on purpose, and coarser the further back it goes. Nobody needs
   * "Updated 247s ago" — past a minute the only question the label answers is
   * "is this roughly current?", and precision there is just noise that changes
   * every second for no reason.
   */
  const lastUpdatedLabel = computed(() => {
    const seconds = secondsSinceUpdate.value
    if (seconds === null) return null
    if (seconds < 5) return 'Updated just now'
    if (seconds < 60) return `Updated ${seconds}s ago`

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `Updated ${minutes}m ago`
    return `Updated ${Math.floor(minutes / 60)}h ago`
  })

  /** The exact time, for the tooltip — the relative label is deliberately vague. */
  const lastUpdatedTitle = computed(() => {
    if (lastUpdatedAt.value === null) return null
    return `Last successful refresh: ${new Date(lastUpdatedAt.value).toLocaleTimeString()}`
  })

  /**
   * Three missed cycles means something is wrong — a dead connection, or a tab
   * that has been in the background (the poller deliberately stops while
   * hidden). Either way the figures on screen are not live, and the user is
   * better off knowing than trusting them.
   */
  const isStale = computed(
    () => secondsSinceUpdate.value !== null && secondsSinceUpdate.value * 1000 > staleAfterMs(),
  )

  /*
   * This composable now lives for the whole life of the app rather than for the
   * life of the jobs table, so the clock skips its write while the tab is
   * hidden: nothing can be reading the label there, and the poller it describes
   * is stopped anyway, so the only thing a background tick could achieve is
   * waking every watcher on `now` once a second forever.
   *
   * The interval itself is left running rather than torn down and rebuilt around
   * visibility, because one no-op callback a second is cheaper than the
   * bookkeeping needed to guarantee it gets restarted.
   */
  const ticker = setInterval(() => {
    if (document.hidden) return
    now.value = Date.now()
  }, 1000)

  onUnmounted(() => {
    clearInterval(ticker)
    clearTimeout(hideTimer)
  })

  return {
    refreshing,
    lastUpdatedAt,
    lastUpdatedLabel,
    lastUpdatedTitle,
    isStale,
    nextRefreshFraction,
    secondsToNextRefresh,
    track,
    markUpdated,
    startCycle,
    stopCycle,
  }
}
